import * as React from 'react';
import { useRouteMatch } from 'react-router-dom';

import { trackEvent } from '@/analytics/trackEvent';
import { useResponsiveConstants } from '@/hooks/responsive/useResponsiveConstants';
import { useDevices } from '@/hooks/useDevices';

import { useGetTrackingInfo } from './useGetTrackingInfo';
import { useToggleCommentsPanel } from './useToggleCommentsPanel';

export const useCommentsClickHandler = ({
  overrideClickHandler,
  cardAction,
  onClick,
}: UseCommentsClickHandlerProps) => {
  const { tab_name, feed_name } = useGetTrackingInfo();

  const previewRoute = useRouteMatch('/preview');
  const { native } = useDevices();
  const { mobile } = useResponsiveConstants();

  const toggleCommentsClickHandler = useToggleCommentsPanel({
    onClick: onClick,
    /*
      with the way the preview routes have been setup ([Modal Routes](https://v5.reactrouter.com/web/example/modal-gallery))
      we have to treat the decision of `pushing` or `replacing` the route to open the comments
      a bit carefully. We navigate back to the feed view by popping the history stack and not by pushing another entry
      into the history stack. This will break the scroll restoration and the feed will load from the top. Because of this
      this history stack should be clean and always the second element from the top of the stack should be the feed

      History stack when just the preview is opened

      |   Preview      |
      +-------------+
      |    Feed        |
      +-------------+
      |                |
      +-------------+

      History stack when the comment opene

      |   Preview (C)  |
      +-------------+
      |    Feed        |
      +-------------+
      |                |
      +-------------+

      History stack when the comment closed


      |   Preview      |
      +-------------+
      |    Feed        |
      +-------------+
      |                |
      +-------------+
     */
    replace: cardAction ? false : previewRoute ? !(native || mobile) : true,
  });

  return React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      trackEvent('comment_icon_clicked', {
        tab_name,
        feed_name,
      });
      if (overrideClickHandler) {
        overrideClickHandler({ comments: true });
      } else {
        toggleCommentsClickHandler(event);
      }
    },
    [overrideClickHandler, toggleCommentsClickHandler, tab_name, feed_name]
  );
};

type UseCommentsClickHandlerProps = {
  overrideClickHandler?: (args: { comments: boolean }) => void;
  cardAction?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};
